// B3-03: while the sample nursery is the active nursery, nothing may be
// created, changed, removed or downloaded. The database enforces this on its
// own; this provider makes the refusal feel deliberate and friendly rather
// than broken, by intercepting the attempt and explaining it.

import { ReactNode, useEffect, useState } from "react";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useExploreMode } from "@/hooks/useExploreMode";
import {
  EXPLORE_BLOCK_MESSAGE,
  EXPLORE_TOAST_TITLE,
  ExploreBlockedError,
  blockIfExploring,
  isReadOnlyRpc,
  setExploreNotifier,
  setExploringNow,
} from "@/lib/exploreBlock";


const WRITE_METHODS = ["insert", "update", "upsert", "delete"] as const;
const STORAGE_WRITE_METHODS = [
  "upload",
  "uploadToSignedUrl",
  "update",
  "remove",
  "move",
  "copy",
  "createSignedUploadUrl",
] as const;

export function ExploreModeProvider({ children }: { children: ReactNode }) {
  const { exploring } = useExploreMode();
  const { toast } = useToast();
  const [followUp, setFollowUp] = useState<string | null>(null);

  useEffect(() => {
    setExploringNow(exploring);
    if (!exploring) setFollowUp(null);
  }, [exploring]);

  useEffect(() => {
    setExploreNotifier((follow) => {
      toast({
        title: EXPLORE_TOAST_TITLE,
        description: EXPLORE_BLOCK_MESSAGE,
      });
      if (follow) setFollowUp(follow);
    });
  }, [toast]);

  useEffect(() => {
    if (!exploring) return;

    const client = supabase as unknown as Record<string, any>;
    const originalFrom = client.from.bind(supabase);
    const originalRpc = client.rpc.bind(supabase);
    const originalInvoke = supabase.functions.invoke.bind(supabase.functions);
    const originalAnchorClick = HTMLAnchorElement.prototype.click;
    const originalPrint = window.print;

    // Table writes.
    client.from = (table: string) => {
      const builder = originalFrom(table) as unknown as Record<string, any>;
      for (const method of WRITE_METHODS) {
        if (typeof builder[method] !== "function") continue;
        builder[method] = () => {
          blockIfExploring();
          throw new ExploreBlockedError();
        };
      }
      return builder;
    };

    // Database actions that are not plain reads.
    client.rpc = (name: string, ...rest: unknown[]) => {
      if (!isReadOnlyRpc(name)) {
        blockIfExploring();
        throw new ExploreBlockedError();
      }
      return originalRpc(name, ...rest);
    };

    // Anything that could send a real email or generate a real file.
    (supabase.functions as unknown as Record<string, any>).invoke = () => {
      blockIfExploring();
      throw new ExploreBlockedError();
    };

    // Uploading, replacing or removing a stored file (policies, certificates).
    const storage = supabase.storage as unknown as Record<string, any>;
    const originalStorageFrom = storage.from.bind(supabase.storage);
    storage.from = (bucket: string) => {
      const api = originalStorageFrom(bucket) as unknown as Record<string, any>;
      for (const method of STORAGE_WRITE_METHODS) {
        if (typeof api[method] !== "function") continue;
        api[method] = () => {
          blockIfExploring();
          throw new ExploreBlockedError();
        };
      }
      return api;
    };


    // Every download on the platform ends in a generated link being clicked.
    HTMLAnchorElement.prototype.click = function patchedClick(this: HTMLAnchorElement) {
      const href = this.getAttribute("href") ?? "";
      const isDownload =
        this.hasAttribute("download") || href.startsWith("blob:") || href.startsWith("data:");
      if (isDownload && blockIfExploring()) return;
      return originalAnchorClick.call(this);
    };

    window.print = () => {
      if (blockIfExploring()) return;
      originalPrint.call(window);
    };

    return () => {
      client.from = originalFrom;
      client.rpc = originalRpc;
      (supabase.functions as unknown as Record<string, any>).invoke = originalInvoke;
      storage.from = originalStorageFrom;
      HTMLAnchorElement.prototype.click = originalAnchorClick;
      window.print = originalPrint;
    };
  }, [exploring]);

  return (
    <>
      {children}
      {exploring && followUp && (
        <div className="fixed bottom-4 right-4 z-[60] max-w-xs rounded-lg border border-nursly-amber/40 bg-white p-3 shadow-none">
          <div className="flex items-start gap-2">
            <p className="flex-1 text-[13px] font-body text-nursly-ink">{followUp}</p>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setFollowUp(null)}
              className="shrink-0 rounded p-1 text-nursly-faint hover:text-nursly-forest"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
